import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberAsMember,
  useMemberInvoiceAsMember,
  useMemberPaymentMethodListAsMember,
  useMutationMemberSaleAsMember,
  useMutationPayInvoiceAsMember,
  useQueryCreditPackAsMember,
  useQueryCreditPackSummaryAsMember,
  useQueryMembershipAsMember,
  useQueryMembershipSummaryAsMember,
} from "@gymflow/api";
import {
  cn,
  formatCurrency,
  LUXON_DATE_FORMAT,
  membershipHelper,
} from "@gymflow/helpers";
import { InvoiceStatus } from "@gymflow/types";
import { useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import z from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes, useRedirectUrl } from "../../hooks";
import useScaModal from "../../hooks/useScaModal";
import { useClubSettings } from "../../providers";
import { ApiResolverContext } from "../../providers/ApiResolverProvider/ApiResolverContext";
import { AuthenticationContext } from "../../providers/authentication/AuthenticationContext";
import { ToastContext } from "../../providers/ToastProvider/context";
import { RouteFeature } from "../../routes";
import useGymflowModels from "../../store";
import { Button, ErrorMessage, LabeledForm, Spinner } from "../atoms";
import { WalletAndPromoCodeBlock } from "../atoms/WalletAndPromoCodeBlock";
import { DatePicker } from "../molecules";
import { PaymentMethodPicker } from "../molecules/PaymentMethodPicker";
import { ShopCartType } from "../Shop/ShopTypes";
import { HostedCheckoutProratedMembershipBanner } from "./components/HostedCheckoutProratedMembershipBanner";
import { HostedPagesBackHeader } from "./components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "./useRedirectIfNotAuthenticated";

export type HostedPagesCheckoutProps = {
  type: ShopCartType["type"];
  onClose?: () => void;
  quickPurchaseFinishedButton?: React.ReactNode;
};

export type CheckoutFormValues = {
  walletAmount?: number;
};

const checkoutSchema = (
  currentWalletBalance: number,
  totalAmountToPay: number,
) =>
  z.object({
    walletAmount: z
      .number()
      .min(0.5, "Min to apply - 0.50")
      .max(currentWalletBalance, "Insufficient amount on balance")
      .max(totalAmountToPay, "Wallet amount cannot exceed the total price")
      .optional(),
  });

export const HostedPagesCheckout: React.FC<HostedPagesCheckoutProps> = ({
  type,
  onClose,
  quickPurchaseFinishedButton,
}) => {
  const [idempotencyKey] = useState<string>(`${uuidv4()}`);
  const { isEmbed, isQuickPurchase } = usePortalRoutes();
  const { t } = useTranslation();
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string>();
  const { userId: memberId } = useContext(AuthenticationContext);
  const { createSiteOrEmbedLink } = usePortalRoutes();

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const { toast } = useContext(ToastContext);
  const { api } = useGymflowModels();
  const { club, properties, featureFlags } = useClubSettings();
  const { clubId } = useContext(ApiResolverContext);
  const { data: userMember } = useMemberAsMember({
    api,
    memberId,
    tz: club.timezone,
  });
  const showScaModal = useScaModal({
    asMember: true,
  });

  const { membershipId, sessionPackId } = useParams<{
    membershipId?: string;
    sessionPackId?: string;
  }>();

  const [promotionCodeInput, setPromotionCodeInput] = useState("");
  const [currentPaymentMethodId, setCurrentPaymentMethodId] =
    useState<string>();
  const [cart, setCart] =
    useState<Exclude<ShopCartType, { type: "GIFT_VOUCHER" }>>();

  const { data: currentWalletBalance } = useQuery(
    apiV2.member.wallet.queries.list(userMember?.id),
  );

  const { data: creditPack, isSuccess: isCreditPackSuccess } =
    useQueryCreditPackAsMember({
      api,
      id:
        type === "CREDIT_PACK" && sessionPackId !== undefined
          ? +sessionPackId
          : undefined,
      enabled: !!api.customerCreditPackApi,
    });
  useEffect(() => {
    if (isCreditPackSuccess) {
      setCart({
        type: "CREDIT_PACK",
        payload: creditPack,
      });
    }
  }, [isCreditPackSuccess, creditPack]);
  const { data: membership, isSuccess: isMembershipSuccess } =
    useQueryMembershipAsMember({
      api,
      id:
        type === "MEMBERSHIP" && membershipId !== undefined
          ? +membershipId
          : undefined,
      enabled: !!api.customerMembershipApi,
    });
  useEffect(() => {
    if (isMembershipSuccess) {
      setCart({
        type: "MEMBERSHIP",
        payload: membership,
      });
      if (
        membership &&
        "fixedStartDate" in membership &&
        !!membership.fixedStartDate
      ) {
        setStartDate(
          DateTime.fromISO(membership.fixedStartDate)
            .setZone(club.timezone)
            .toISODate()!,
        );
      }
    }
  }, [club.timezone, isMembershipSuccess, membership, properties]);
  const gymOpeningDate = DateTime.fromFormat(
    properties.gymOpeningDate,
    "yyyy-MM-dd",
    { zone: club.timezone },
  );
  const isStartDayInTheFuture =
    gymOpeningDate > DateTime.now().setZone(club.timezone);
  const [startDate, setStartDate] = useState<string>(
    (cart && "fixedStartDate" in cart.payload && !!cart.payload.fixedStartDate
      ? DateTime.fromISO(cart.payload.fixedStartDate)
      : isStartDayInTheFuture
        ? gymOpeningDate
        : DateTime.now().setZone(club.timezone).startOf("day")
    ).toFormat(LUXON_DATE_FORMAT),
  );
  const [promotionCode, setPromotionCode] = useState<string>();
  const { data: creditPackSummary, isFetching: creditPackSummaryIsFetching } =
    useQueryCreditPackSummaryAsMember({
      api,
      opts: {
        sessionPackId: cart?.type === "CREDIT_PACK" ? cart.payload.id : 0,
        promotionCode: promotionCode,
      },
      enabled: cart?.type === "CREDIT_PACK",
    });
  const { data: membershipSummary, isFetching: membershipSummaryIsFetching } =
    useQueryMembershipSummaryAsMember({
      api,
      opts: {
        startDate,
        membershipId: cart?.type === "MEMBERSHIP" ? cart.payload.id : 0,
        promotionCode: promotionCode,
      },
      enabled: cart?.type === "MEMBERSHIP",
    });

  const isFetchingSummary =
    creditPackSummaryIsFetching || membershipSummaryIsFetching; //|| productSummaryIsFetching;
  const summary = creditPackSummary || membershipSummary; // || productSummary;
  const { data: invoice } = useMemberInvoiceAsMember({
    api,
    invoiceNumber: currentInvoiceId,
  });

  const { mutateAsync: payFailed } = useMutationPayInvoiceAsMember({
    api,
  });
  const { mutateAsync: checkout } = useMutationMemberSaleAsMember({
    api,
    clubId: clubId,
  });

  // for future: need to handle all useState in form
  const formik = useFormik<CheckoutFormValues>({
    initialValues: { walletAmount: undefined },
    validationSchema: toFormikValidationSchema(
      checkoutSchema(
        currentWalletBalance?.walletBalance || 0,
        summary?.upfrontPriceDetails.totalAmountToPay || 0,
      ),
    ),
    onSubmit: async (values) => await onPay(values),
  });

  const { values } = formik;

  const onPay = async (values: CheckoutFormValues) => {
    if (!cart) return;

    let result: { invoiceNumber: string; invoiceStatus: InvoiceStatus };

    const paymentMethod = shouldCollectPaymentMethod
      ? currentPaymentMethodId
      : undefined;

    // empty string or undefined or 0 -> undefined
    const walletAmount = !values.walletAmount ? undefined : values.walletAmount;

    if (cart.type === "MEMBERSHIP") {
      result = await checkout({
        paymentMethod,
        promotionCode,
        startDate,
        membershipId: cart.payload.id,
        idempotencyKey,
        walletAmount,
      });
    } else if (cart.type === "CREDIT_PACK") {
      result = await checkout({
        paymentMethod,
        promotionCode,
        startDate,
        sessionPackId: cart.payload.id,
        idempotencyKey,
        walletAmount,
      });
    } else {
      // PRODUCT
      result = await checkout({
        paymentMethod,
        promotionCode,
        products: cart.payload.map((e) => ({
          productId: e.product.id,
          quantity: e.quantity,
        })),
        idempotencyKey,
        walletAmount,
      });
    }
    setCurrentInvoiceId(result.invoiceNumber);
    showScaModal({ invoiceNumber: result.invoiceNumber });
  };

  const { redirectToCustomizedWebsiteOr } = useRedirectUrl();
  useEffect(() => {
    if (
      (invoice?.status === "PAID" || invoice?.status === "PROCESSING") &&
      !isQuickPurchase
    ) {
      toast({
        message: t("pages.hostedPagesCheckout.invoicePaid"),
        description: t("pages.hostedPagesCheckout.invoicePaidDescription"),
        intent: "success",
      });
      setTimeout(() => {
        redirectToCustomizedWebsiteOr();
      }, 1500);
    }
  }, [
    invoice?.status,
    isQuickPurchase,
    redirectToCustomizedWebsiteOr,
    t,
    toast,
  ]);
  const isFree = summary?.upfrontPriceDetails?.totalAmountToPay === 0;
  const shouldCollectPaymentMethod =
    !isFree ||
    (cart?.type === "MEMBERSHIP" && cart?.payload?.type === "RECURRING");
  const [parent] = useAutoAnimate();
  const { status: paymentsLoadingStatus } = useMemberPaymentMethodListAsMember({
    api,
    clubId,
    memberId,
  });
  const arePaymentMethodsLoading =
    !paymentsLoadingStatus || paymentsLoadingStatus === "pending";
  const showFixedStartDateInPastError = !!(
    cart?.type === "MEMBERSHIP" &&
    cart.payload.fixedStartDate &&
    DateTime.fromISO(cart.payload.fixedStartDate)
      .setZone(club.timezone)
      .endOf("day") < DateTime.now()
  );
  return (
    <div
      className={cn(
        "flex w-full flex-col items-center overflow-y-auto bg-gray-50 dark:bg-gray-800",
        {
          "track-height": !isQuickPurchase,
          "h-fit": !isQuickPurchase && isEmbed,
          "h-full": isQuickPurchase || !isEmbed,
        },
      )}
    >
      <HostedPagesBackHeader
        label={t("portal.common.checkout")}
        onClose={onClose}
      />
      <div
        ref={parent}
        className="flex h-fit w-full max-w-xl flex-col gap-4 px-4 py-6"
      >
        {invoice && (
          <div className="flex w-full flex-col items-center gap-y-3">
            <div className="dark:text-gray-0 text-3xl font-bold text-gray-950">
              {formatCurrency(invoice.totalAmount, club.defaultCurrency)}
            </div>
            <div className="flex flex-col items-center gap-1">
              {(invoice.status === "PAID" ||
                invoice.status === "PROCESSING") && (
                <>
                  <div className="text-success-600 dark:text-success-400 text-center font-bold">
                    {t("pages.hostedPagesCheckout.paymentSuccessful")}
                  </div>
                  <div className="font-medium text-gray-600 dark:text-gray-400">
                    {DateTime.fromISO(
                      invoice.issueDate ?? new Date().toISOString(),
                    ).toLocaleString(DateTime.DATE_MED)}
                  </div>
                </>
              )}
              {invoice.status === "PAST_DUE" && (
                <div className="flex flex-col items-center gap-3">
                  <div className="text-error-600 dark:text-error-400 text-center font-bold">
                    {t("pages.hostedPagesCheckout.paymentFailed")}
                  </div>
                  <div className="font-medium text-gray-600 dark:text-gray-400">
                    <Button
                      onClick={async () => {
                        const newInvoice = await payFailed({
                          invoiceNumber: invoice.number,
                          paymentMethodId: currentPaymentMethodId,
                        });
                        showScaModal({ invoiceNumber: newInvoice.number });
                      }}
                    >
                      {t("pages.hostedPagesCheckout.retry")}
                    </Button>
                  </div>
                </div>
              )}
              {invoice.status === "AWAITING_AUTHORIZATION" && (
                <div className="text-warning-600 text-center font-bold">
                  {t("pages.hostedPagesCheckout.authorize")}
                </div>
              )}
            </div>
          </div>
        )}
        {shouldCollectPaymentMethod && userMember && (
          <div className="max-w-full">
            <PaymentMethodPicker
              userMemberId={userMember.id}
              currentPaymentMethodId={currentPaymentMethodId}
              onChange={setCurrentPaymentMethodId}
              disabled={!!invoice}
              allowBankPayment={
                cart?.type === "MEMBERSHIP" && cart.payload.type === "RECURRING"
              }
              asMember
            />
          </div>
        )}
        {properties.allowCustomerSelectingStartDate &&
          !isStartDayInTheFuture &&
          cart?.type === "MEMBERSHIP" &&
          !cart.payload.fixedStartDate && (
            <LabeledForm label={t("pages.hostedPagesCheckout.startDate")}>
              <DatePicker
                inputClassnames="dark:bg-gray-900 dark:hover:!bg-gray-900"
                label={DateTime.fromFormat(startDate, LUXON_DATE_FORMAT)
                  .setZone("local", { keepLocalTime: true })
                  .toLocaleString(DateTime.DATE_MED)}
                mode="single"
                modifiers={{
                  disabled: (d) => {
                    if (!membership?.fixedStartDate) return false;
                    const selected = DateTime.fromFormat(
                      startDate,
                      LUXON_DATE_FORMAT,
                    ).setZone("local", { keepLocalTime: true });
                    return !selected.hasSame(DateTime.fromJSDate(d), "day");
                  },
                }}
                hidden={{
                  before: DateTime.now().toJSDate(),
                }}
                selected={DateTime.fromFormat(startDate, LUXON_DATE_FORMAT)
                  .setZone("local", { keepLocalTime: true })
                  .toJSDate()}
                handleDateSave={(newDate: Date) => {
                  if (!newDate) {
                    return;
                  }
                  setStartDate(
                    DateTime.fromJSDate(newDate).toFormat(LUXON_DATE_FORMAT),
                  );
                }}
              />
            </LabeledForm>
          )}
        {!!invoice && promotionCode && (
          <div className="flex flex-row items-center justify-between">
            <div className="text-base text-gray-500">
              {t("pages.hostedPagesCheckout.promoCode")}
            </div>
            <div className="text-base font-bold text-gray-950 dark:text-gray-50">
              {promotionCode}
            </div>
          </div>
        )}
        {!invoice &&
          (featureFlags.FE_USER_WALLETS ? (
            <WalletAndPromoCodeBlock
              promotionCodeInput={promotionCodeInput}
              setPromotionCode={setPromotionCode}
              setPromotionCodeInput={setPromotionCodeInput}
              promotionCode={promotionCode}
              summary={summary}
              isFetchingSummary={isFetchingSummary}
              walletBalance={currentWalletBalance?.walletBalance || 0}
              formik={formik}
            />
          ) : (
            <LabeledForm label={t("pages.hostedPagesCheckout.promoCode")}>
              <div className="bg-gray-0 inline-flex h-10 flex-row items-start justify-start rounded-lg border border-gray-300 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="bg-gray-0 flex h-full flex-1 items-center justify-start gap-2 overflow-hidden rounded-lg rounded-r-none border-r border-gray-300 dark:border-gray-700 dark:bg-gray-950">
                  <input
                    placeholder={t("pages.hostedPagesCheckout.enterCode")}
                    className="bg-gray-0 size-full px-3 py-2 text-gray-500 outline-none dark:bg-gray-950"
                    value={promotionCodeInput ?? ""}
                    onChange={(e) => {
                      setPromotionCodeInput(e.target.value);
                    }}
                    disabled={promotionCode !== undefined}
                  />
                </div>
                <Button
                  onClick={() => {
                    if (!promotionCode) {
                      setPromotionCode(promotionCodeInput);
                    } else {
                      setPromotionCode(undefined);
                      setPromotionCodeInput("");
                    }
                  }}
                  className="h-full min-h-0 !rounded-lg !rounded-l-none !border-none focus:!outline-none"
                >
                  {isFetchingSummary ? (
                    <Spinner />
                  ) : promotionCode === undefined ? (
                    t("portal.common.apply")
                  ) : (
                    t("portal.common.remove")
                  )}
                </Button>
              </div>
            </LabeledForm>
          ))}
        <div className="flex w-full border-b-2 border-dashed border-gray-400 dark:border-gray-600" />
        <div className="flex w-full flex-col gap-2">
          {!featureFlags.FE_USER_WALLETS &&
            summary &&
            promotionCode &&
            summary?.validPromotionCode !== "YES" && (
              <div className="flex w-full flex-row justify-between font-semibold text-red-500">
                {t("pages.hostedPagesCheckout.promoCodeInvalid")}
              </div>
            )}
          {summary && cart && cart.type === "CREDIT_PACK" && (
            <div className="flex w-full flex-row justify-between">
              <div className="font-medium text-gray-500">
                {cart.payload.name}
              </div>
              <div className="font-medium text-gray-500">
                {formatCurrency(
                  summary.upfrontPriceDetails.totalAmountBeforeDiscount,
                  club.defaultCurrency,
                )}
              </div>
            </div>
          )}
          {membershipSummary && cart && cart.type === "MEMBERSHIP" && (
            <>
              <div className="flex w-full flex-row justify-between">
                <div className="font-medium text-gray-500">
                  {cart.payload.name}
                </div>
                <div className="font-medium text-gray-500">
                  {formatCurrency(
                    cart.payload.type === "RECURRING"
                      ? membershipSummary.recurringMembershipAndAddonsProratedAmount
                      : membershipSummary.prepaidMembershipAmount,
                    club.defaultCurrency,
                  )}
                </div>
              </div>
              {membershipSummary.addonsProratedList?.map((e) => (
                <div
                  className="flex w-full flex-row justify-between"
                  key={e.id}
                >
                  <div className="font-medium text-gray-500">{e.name}</div>
                  <div className="font-medium text-gray-500">
                    + {formatCurrency(e.amount, club.defaultCurrency)}
                    {e.quantity > 1 && (
                      <>
                        {" x "}
                        {e.quantity}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
          {summary &&
            cart &&
            cart.type === "PRODUCT" &&
            cart.payload.map((e) => (
              <div
                key={e.product.id}
                className="dark:text-gray-0 flex w-full flex-row justify-between text-gray-950"
              >
                <div>{e.product.name}</div>
                <div className="flex flex-row gap-1">
                  <div>{e.quantity} x</div>
                  <div className="font-medium">
                    {formatCurrency(e.product.price, club.defaultCurrency)}
                  </div>
                </div>
              </div>
            ))}
          {!!summary &&
            summary.validPromotionCode === "YES" &&
            "promotionUpfrontAmount" in summary &&
            summary.promotionUpfrontAmount !== undefined && (
              <div className="text-success-600 dark:text-success-400 flex w-full flex-row justify-between">
                <div className="font-medium">
                  {t("pages.hostedPagesCheckout.upfrontDiscount")}
                </div>
                <div className="font-medium">
                  {summary.promotionUpfrontDiscountType === "CURRENCY"
                    ? formatCurrency(
                        -summary.promotionUpfrontAmount,
                        club.defaultCurrency,
                      )
                    : `-${summary.promotionUpfrontAmount}%`}
                </div>
              </div>
            )}
          {!!summary &&
            summary.validPromotionCode === "YES" &&
            "promotionRecurringAmount" in summary &&
            "promotionRecurringDiscountType" in summary &&
            "promotionRecurringAmount" in summary &&
            summary.promotionRecurringAmount !== undefined && (
              <div className="text-success-600 dark:text-success-400 flex w-full flex-row justify-between">
                <div className="font-medium">
                  {t("pages.hostedPagesCheckout.recurringDiscount")}
                </div>
                <div className="font-medium">
                  {summary.promotionRecurringDiscountType === "CURRENCY"
                    ? formatCurrency(
                        -(summary.promotionRecurringAmount ?? 0),
                        club.defaultCurrency,
                      )
                    : `-${summary.promotionRecurringAmount}%`}
                </div>
              </div>
            )}
          {featureFlags.FE_USER_WALLETS &&
            !!summary &&
            !!values.walletAmount && (
              <div className="text-success-600 dark:text-success-400 flex w-full flex-row justify-between">
                <div className="font-medium">
                  {t("pages.hostedPagesCheckout.walletBalance")}
                </div>
                <div className="font-medium">
                  {formatCurrency(-values.walletAmount, club.defaultCurrency)}
                </div>
              </div>
            )}
          {summary && (
            <div className="dark:text-gray-0 flex w-full flex-row items-center justify-between text-gray-950">
              <div className="text-xl font-bold">
                {t("pages.hostedPagesCheckout.amountDue")}
              </div>
              <div className="text-xl font-bold">
                {formatCurrency(
                  summary.upfrontPriceDetails.totalAmountToPay -
                    (values.walletAmount || 0),
                  club.defaultCurrency,
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex w-full flex-col gap-3">
          {cart?.type === "MEMBERSHIP" && (
            <HostedCheckoutProratedMembershipBanner
              membership={cart.payload}
              startDate={startDate}
            />
          )}
          {!invoice && (
            <div className="flex w-full flex-col gap-1">
              {cart?.type === "MEMBERSHIP" &&
                userMember?.subscriptions.some(
                  membershipHelper.isCurrentSubscription,
                ) && (
                  <ErrorMessage
                    text={t("pages.hostedPagesCheckout.alreadySubscribed")}
                  />
                )}
              {showFixedStartDateInPastError && (
                <ErrorMessage
                  text={t("pages.hostedPagesCheckout.fixedStartDateOnPast")}
                />
              )}

              {!arePaymentMethodsLoading &&
                !currentPaymentMethodId &&
                shouldCollectPaymentMethod && (
                  <ErrorMessage
                    text={t("pages.hostedPagesCheckout.selectPaymentMethod")}
                  />
                )}
              <Button
                intent="secondary"
                className="w-full"
                disabled={
                  !formik.isValid ||
                  (!currentPaymentMethodId && shouldCollectPaymentMethod) ||
                  !summary ||
                  (cart?.type === "MEMBERSHIP" &&
                    userMember?.subscriptions.some(
                      membershipHelper.isCurrentSubscription,
                    )) ||
                  showFixedStartDateInPastError
                }
                onClick={async () => await formik.submitForm()}
              >
                {isFree
                  ? t("portal.common.getForFree")
                  : t("portal.common.payAmount", {
                      amount: formatCurrency(
                        (summary?.upfrontPriceDetails.totalAmountToPay ?? 0) -
                          (values.walletAmount || 0),
                        club.defaultCurrency,
                      ),
                    })}
              </Button>
            </div>
          )}
        </div>
        {isQuickPurchase &&
          (invoice?.status === "PAID" || invoice?.status === "PROCESSING") &&
          quickPurchaseFinishedButton}
      </div>
    </div>
  );
};
